import { IServerSideGetRowsParams } from '@ag-grid-community/core';
import { Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { ExportsMonitoringChannel } from 'exports/interfaces/exports-monitoring-channel.interface';
import { Observable, map, of, switchMap } from 'rxjs';
import { GridService } from 'shared/interfaces/grid-service';
import { CachingService } from 'shared/services';

@Injectable({
    providedIn: 'root',
})
export class ExportsMonitoringService implements GridService {
    private apollo: ApolloBase;
    NAME = 'ExportsMonitoringService';

    constructor(
        private apolloProvider: Apollo,
        private cachingService: CachingService,
    ) {
        this.apollo = this.apolloProvider.use('distributionManagementAPI');
    }

    channelId = signal<string | null>(null);
    channelId$ = toObservable(this.channelId);
    lastUpdatedAtTimestamp = signal<number | null>(null);

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        const orderBy = params.request.sortModel[0]?.colId ?? 'name';
        const orderDirection = params.request.sortModel[0]?.sort?.toUpperCase() ?? 'ASC';

        return this.apollo
            .query({
                query: gql`
                    query getChannelExportInformation($orderBy: String, $orderDirection: OrderDirection, $pageNumber: Int, $pageSize: Int) {
                        channelInformation {
                            searchChannelExportInformation(
                                searchQuery: {
                                    pageNumber: $pageNumber
                                    pageSize: $pageSize
                                    orderBy: $orderBy
                                    orderDirection: $orderDirection
                                }
                                channelExportInformationFilter: {}
                            ) {
                                data {
                                    id
                                    lastExportDateOutput
                                    successRate
                                    channelLogo
                                    channel {
                                        id
                                        name
                                    }
                                }
                                totalCount
                            }
                        }
                    }
                `,
                variables: {
                    pageNumber,
                    pageSize,
                    orderBy,
                    orderDirection,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    const returnValue = response.data.channelInformation.searchChannelExportInformation;
                    const channels: ExportsMonitoringChannel[] = returnValue.data.map((element: any) => {
                        const channel = {
                            id: element.id,
                            channelId: element.channel.id,
                            channelName: element.channel.name,
                            channelLogoUrl: element.channelLogo,
                            lastExportDateOutput: element.lastExportDateOutput,
                            successRate: element.successRate,
                            group: true,
                        };
                        return channel;
                    });
                    return { data: channels, totalCount: returnValue.totalCount };
                }),
            );
    }

    singularChannelData = toSignal<ExportsMonitoringChannel | null>(
        this.channelId$.pipe(
            switchMap((channelId) => {
                if (!channelId) {
                    return of(null);
                }
                return this.getOneRowByChannelId(channelId);
            }),
        ),
    );

    public getOneRowByChannelId(channelId: string): Observable<ExportsMonitoringChannel | null> {
        if (!channelId) {
            return of(null);
        }

        return this.apollo
            .query({
                query: gql`
                    query getChannelExportInformation {
                        channelInformation {
                            searchChannelExportInformation(
                                searchQuery: { pageNumber: 1, pageSize: 20 }
                                channelExportInformationFilter: { searchTerm: "${channelId}" }
                            ) {
                                data {
                                    id
                                    lastExportDateOutput
                                    successRate
                                    channelLogo
                                    channel {
                                        id
                                        name
                                    }
                                }
                                totalCount
                            }
                        }
                    }
                `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    const returnValue = response.data.channelInformation.searchChannelExportInformation;
                    const channels: ExportsMonitoringChannel[] = returnValue.data.map((element: any) => {
                        const channel = {
                            id: element.id,
                            channelId: element.channel.id,
                            channelName: element.channel.name,
                            channelLogoUrl: element.channelLogo,
                            lastExportDateOutput: element.lastExportDateOutput,
                            successRate: element.successRate,
                            group: true,
                        };
                        return channel;
                    });
                    return channels[0];
                }),
            );
    }

    public getLastProcessingDate() {
        return this.apollo
            .query({
                query: gql`
                    query getLastProcessingDate {
                        channelInformation {
                            getLastProcessingDate {
                                data {
                                    lastProcessingDateOutput
                                }
                            }
                        }
                    }
                `,
            })
            .pipe(
                map((response: any) => {
                    const timestamp = response?.data?.channelInformation?.getLastProcessingDate?.data?.[0]?.lastProcessingDateOutput;
                    this.lastUpdatedAtTimestamp.set(timestamp);
                }),
            );
    }
}
